import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, ArrowRight } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Public landing page after Razorpay redirects back from the payment app.
 *
 * Razorpay appends `?razorpay_payment_id=…&razorpay_order_id=…&razorpay_signature=…`
 * to the callback_url on success. When the user lands here we poll
 * `/api/smb/activate/{token}/status` until the webhook has flipped the
 * lead to `activation_paid` (typically <5s), then push them straight
 * into the details form.
 *
 * Two-mode behaviour:
 *   • Form not yet submitted → CTA: "Continue to website details"
 *   • Form already submitted → final confirmation message.
 */
export default function ActivationThankYouPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(true);

  const paymentId = searchParams.get('razorpay_payment_id') || searchParams.get('order_id');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let verified = false;

    // Belt-and-suspenders: if Razorpay redirected back with signed
    // payment query params, verify them directly against our backend.
    // This unblocks the lead instantly even if the Razorpay → backend
    // webhook hasn't been configured / fired yet.
    const verifyIfPossible = async () => {
      if (verified) return;
      const pid = searchParams.get('razorpay_payment_id');
      const oid = searchParams.get('razorpay_order_id');
      const s = searchParams.get('razorpay_signature');
      if (!pid || !oid || !s) return;
      try {
        await fetch(`${BASE}/api/smb/activate/${token}/verify-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            razorpay_payment_id: pid,
            razorpay_order_id: oid,
            razorpay_signature: s,
          }),
        });
        verified = true;
      } catch {/* ignore — status poll will catch up */}
    };

    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      // Fire verify on first 3 attempts (in case Razorpay's redirect
      // beat our backend processing by a few ms).
      if (attempts <= 3) await verifyIfPossible();
      try {
        const r = await fetch(`${BASE}/api/smb/activate/${token}/status`);
        if (r.ok) {
          const j = await r.json();
          if (cancelled) return;
          setStatus(j.status || null);
          if (j.status === 'activation_paid' || j.status === 'details_submitted'
              || j.status === 'in_development' || j.status === 'awaiting_approval'
              || j.status === 'approved' || j.status === 'paid' || j.status === 'website_live') {
            setPolling(false);
            return;
          }
        }
      } catch {/* ignore */}
      if (attempts < 20 && !cancelled) {
        setTimeout(tick, 1500);
      } else {
        setPolling(false);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [token, searchParams]);

  const formDone = ['details_submitted', 'in_development', 'awaiting_approval',
                    'approved', 'paid', 'website_live'].includes(status);
  const paidNeedsForm = status === 'activation_paid';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center space-y-4" data-testid="activation-thank-you">
        <div className="inline-flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
          {polling && !status ? <Loader2 className="h-8 w-8 animate-spin" /> : <Check className="h-8 w-8" />}
        </div>

        {polling && !status && (
          <>
            <h1 className="text-2xl font-bold">Confirming your payment…</h1>
            <p className="text-muted-foreground text-sm">
              This usually takes a few seconds. Please don&apos;t close this page.
            </p>
          </>
        )}

        {paidNeedsForm && (
          <>
            <h1 className="text-2xl font-bold">Payment received!</h1>
            <p className="text-muted-foreground text-sm">
              Thank you. Next — share your business details so we can start building your website.
              It only takes a few minutes.
            </p>
            <Button onClick={() => navigate(`/activate/${token}/form`)}
                    data-testid="continue-to-form" className="w-full">
              Continue to website details <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </>
        )}

        {formDone && (
          <>
            <h1 className="text-2xl font-bold">All set!</h1>
            <p className="text-muted-foreground text-sm">
              We&apos;ve received your details. Our team will start building your website right
              away and will reach out shortly on WhatsApp with the next update.
            </p>
            <p className="text-xs text-muted-foreground pt-2">You can close this page.</p>
          </>
        )}

        {!polling && !status && (
          <>
            <h1 className="text-2xl font-bold">Payment in progress</h1>
            <p className="text-muted-foreground text-sm">
              We&apos;re still waiting on confirmation from Razorpay. If you completed the
              payment, please refresh this page in a minute. If you abandoned, you can return
              to the activation page to retry.
            </p>
          </>
        )}

        {paymentId && (
          <p className="text-[10px] text-muted-foreground/60 pt-2 break-all">
            Reference: {paymentId}
          </p>
        )}
      </Card>
    </div>
  );
}
